import api from './api.ts';

export const META_CRM_STAGES = ['lead', 'agendado', 'faltou', 'comparecimento', 'venda', 'perdido'] as const;
export type MetaCrmStage = typeof META_CRM_STAGES[number];
export type MetaCrmActionSource = 'business_messaging' | 'physical_store' | 'unknown';

export interface MetaCrmConfigUpdate {
  capture_enabled?: boolean;
  delivery_enabled?: boolean;
  dataset_id?: string | null;
  waba_id?: string | null;
  ad_account_id?: string | null;
  allowed_stages?: MetaCrmStage[];
  currency?: 'BRL';
}

export interface MetaCrmConfig extends Required<MetaCrmConfigUpdate> {
  policy_verified: boolean;
  verified_at: string | null;
  updated_at: string | null;
}

export interface MetaCrmConfigResponse {
  available: boolean;
  config: MetaCrmConfig;
  delivery_operational: boolean;
  missing_capabilities: string[];
  stages: MetaCrmStage[];
}

export interface MetaCrmDiagnosis {
  verified: boolean;
  missing_capabilities: string[];
  action_sources?: Partial<Record<'business_messaging' | 'physical_store', { verified: boolean; missing_capabilities: string[] }>>;
}

export interface MetaCrmSetup {
  available: boolean;
  selected_assets: { ad_account_id: string | null; pixel_id: string | null; waba_id: string | null };
  defaults: { ad_account_id: string | null; dataset_id: string | null; waba_id: string | null; currency: 'BRL' };
  dataset_verified: boolean;
  missing_capabilities: string[];
}

export async function getMetaCrmSetup(clinicId: number, signal?: AbortSignal): Promise<MetaCrmSetup> {
  return (await api.get<MetaCrmSetup>('/api/integrations/meta-crm/setup', { params: { clinic_id: clinicId }, signal })).data;
}

export interface MetaCrmEvent {
  id: number;
  stage: MetaCrmStage;
  occurred_at: string;
  // Decimal serializado pelo backend: preservar precisão do valor faturado.
  value: string | null;
  currency: string;
  action_source: MetaCrmActionSource;
  attribution_id: number | null;
  status: string;
  delivery_status: string | null;
  attempts: number;
  completed_at: string | null;
  reason: string | null;
}

export interface MetaCrmSummary {
  available: boolean;
  total: number;
  by_stage: Record<MetaCrmStage, number>;
  by_status: Record<string, number>;
}

export interface MetaCrmEventsResponse {
  available: boolean;
  items: MetaCrmEvent[];
  next_cursor: number | null;
}

export interface MetaCrmEventsQuery {
  limit?: number;
  before_id?: number;
  stage?: MetaCrmStage;
}

export interface MetaCrmAttribution {
  id: number;
  provider: 'official' | 'official_whatsapp';
  occurred_at: string;
}

export interface MetaCrmAttributionsResponse {
  available: boolean;
  items: MetaCrmAttribution[];
  next_cursor: number | null;
}

export type MetaCrmAttributionsQuery = Pick<MetaCrmEventsQuery, 'limit' | 'before_id'>;

// O backend valida a clínica selecionada contra as permissões da sessão.
const base = '/api/integrations/meta-crm';

export async function getMetaCrmConfig(clinicId: number, signal?: AbortSignal): Promise<MetaCrmConfigResponse> {
  return (await api.get<MetaCrmConfigResponse>(`${base}/config`, { params: { clinic_id: clinicId }, signal })).data;
}

export async function updateMetaCrmConfig(clinicId: number, payload: MetaCrmConfigUpdate): Promise<MetaCrmConfigResponse> {
  return (await api.put<MetaCrmConfigResponse>(`${base}/config`, payload, { params: { clinic_id: clinicId } })).data;
}

export async function diagnoseMetaCrm(clinicId: number, signal?: AbortSignal): Promise<MetaCrmDiagnosis> {
  return (await api.post<MetaCrmDiagnosis>(`${base}/diagnose`, undefined, { params: { clinic_id: clinicId }, signal })).data;
}

export async function getMetaCrmSummary(clinicId: number, signal?: AbortSignal): Promise<MetaCrmSummary> {
  return (await api.get<MetaCrmSummary>(`${base}/summary`, { params: { clinic_id: clinicId }, signal })).data;
}

export async function getMetaCrmEvents(clinicId: number, query: MetaCrmEventsQuery = {}, signal?: AbortSignal): Promise<MetaCrmEventsResponse> {
  const { limit, before_id, stage } = query;
  return (await api.get<MetaCrmEventsResponse>(`${base}/events`, {
    params: { clinic_id: clinicId, limit, before_id, stage }, signal,
  })).data;
}

export async function getMetaCrmEventAttributions(clinicId: number, eventId: number, query: MetaCrmAttributionsQuery = {}, signal?: AbortSignal): Promise<MetaCrmAttributionsResponse> {
  if (!Number.isSafeInteger(eventId) || eventId <= 0) throw new Error('ID de evento inválido');
  const { limit, before_id } = query;
  return (await api.get<MetaCrmAttributionsResponse>(`${base}/events/${eventId}/attributions`, {
    params: { clinic_id: clinicId, limit, before_id }, signal,
  })).data;
}

export async function classifyMetaCrmEvent(clinicId: number, eventId: number, actionSource: MetaCrmActionSource, attributionId?: number | null): Promise<MetaCrmEvent> {
  if (!Number.isSafeInteger(eventId) || eventId <= 0) throw new Error('ID de evento inválido');
  if (attributionId != null && (!Number.isSafeInteger(attributionId) || attributionId <= 0)) {
    throw new Error('ID de atribuição inválido');
  }
  // Omitir preserva o vínculo; null remove; um ID seleciona a atribuição validada pelo backend.
  const payload = attributionId === undefined
    ? { action_source: actionSource }
    : { action_source: actionSource, attribution_id: attributionId };
  return (await api.patch<MetaCrmEvent>(`${base}/events/${eventId}`, payload, { params: { clinic_id: clinicId } })).data;
}
